import React from 'react'
import styled from 'styled-components'

import { Alert, Dialog } from '@mote/ui'
import { Block, BlockGroup, Button, Dropzone, Text } from '@mote/ui'
import { useUploadCSVMutation } from '@mote/api'
import { EmailMeMyUsersButton } from '../emailMeMyUsersButton'
import { DownloadCSVTemplateButton } from '../downloadCSVTemplateButton'

export const ImportCSVDialog = ({ onDialogDismissal, onUploadSuccess }) => {
  const [fileName, setFileName] = React.useState(null)
  const {
    mutate: uploadCSV,
    data: uploadCSVData,
    loading: uploadCSVLoading,
    flush: flushUploadCSVResponse
  } = useUploadCSVMutation()

  const uploadSuccess = React.useMemo(() => {
    if (uploadCSVData === null) return null

    return uploadCSVData?.success
  }, [uploadCSVData])

  const uploadValidationErrors = React.useMemo(() => {
    if (uploadCSVData === null) return false

    return Boolean(!uploadCSVData.success)
  }, [uploadCSVData])

  function readFile(file) {
    const reader = new FileReader()
    reader.onload = async function (e) {
      await uploadCSV({ variables: { CSV: e.target.result } })
    }
    reader.readAsText(file)
  }
  function handleFilesAdded(files) {
    if (!files.length) return

    const csvFile = files[0]
    readFile(csvFile)
    setFileName(files[0].name)
  }

  React.useEffect(() => {
    if (!uploadCSVData || !uploadCSVData?.success) return

    onUploadSuccess({
      fileName,
      jobId: uploadCSVData.jobId,
      userCount: uploadCSVData.userCount
    })
  }, [uploadCSVData, fileName, onUploadSuccess])

  return (
    <Dialog isOpen onClose={onDialogDismissal} hasBackdrop={false}>
      <Dialog.Heading>Import CSV</Dialog.Heading>
      <Dialog.Content>
        <Block align='center' gap={14}>
          {!uploadValidationErrors && (
            <Dropzone
              maxFileSize={4 * 1024 * 1024}
              message='Drag CSV Here'
              // mimeTypes={['text/csv']}
              mimeTypes={[]}
              loadingMessage='Validating...'
              fileCountLimit={1}
              loading={uploadCSVLoading}
              onFilesAdded={handleFilesAdded}
            />
          )}

          {uploadValidationErrors && (
            <Block gap={8} spreadX>
              <Alert
                message={`We found ${uploadCSVData?.errorLines.length} error${
                  uploadCSVData.errorLines.length > 1 ? 's' : ''
                }`}
              />

              <ErrorsContainer>
                {uploadCSVData.errorLines.slice(0, 100).map((error, index) => (
                  <Error key={index}>
                    <ErrorCount>#{index + 1}</ErrorCount> {error}
                  </Error>
                ))}
              </ErrorsContainer>
            </Block>
          )}

          <BlockGroup gap={15}>
            <Block layout='horizontal' justify='space-between' spreadX>
              <Block gap={4} width={275}>
                <Text tag='p' fontSize={14} fontWeight={700}>
                  Start afresh with a template
                </Text>

                <Text tag='small' fontSize={12} color='#646F7B'>
                  We rely on a specific format for CSV uploads. Our template can
                  help you get started.{' '}
                  <SupportLink
                    href='https://support.mote.com/hc/en-us/articles/4406160266772'
                    target='_new'
                  >
                    Learn more
                  </SupportLink>
                </Text>
              </Block>

              <Block justify='flex-end' align='flex-end' grow>
                <DownloadCSVTemplateButton />
              </Block>
            </Block>
            <Alert
              type='info'
              fontSize={12}
              message="Don't forget to include your own email address and role (owner/admin)"
            />
          </BlockGroup>

          <Separator />

          <BlockGroup layout='horizontal' justify='space-between' spreadX>
            <Block gap={4} width={266}>
              <Text tag='p' fontSize={14} fontWeight={700}>
                Edit or add to existing users
              </Text>

              <Text tag='small' fontSize={12} color='#646F7B'>
                We can email you a list of your current user database to amend
                and re-upload
              </Text>
            </Block>

            <Block justify='flex-end' align='flex-end' grow>
              <EmailMeMyUsersButton />
            </Block>
          </BlockGroup>
          {(uploadValidationErrors || uploadSuccess) && <Separator />}
        </Block>
      </Dialog.Content>

      {uploadValidationErrors && (
        <>
          <Dialog.ButtonGroup alignment='flex-end'>
            <Button
              text='Retry Import'
              size='sm'
              onClick={flushUploadCSVResponse}
            />
            <Button
              variant='secondary'
              text='Cancel'
              size='sm'
              onClick={onDialogDismissal}
            />
          </Dialog.ButtonGroup>
        </>
      )}
    </Dialog>
  )
}

const Separator = styled.div`
  background: rgba(213, 212, 218, 0.5);
  height: 1px;
  width: 100%;
`

const ErrorsContainer = styled(Block)`
  border: 1px solid #f44336;
  border-radius: 7px;
  padding: 11px 16px;
  height: 95px;
  overflow-y: auto;
`

const Error = styled.span`
  font-size: 14px;
  line-height: 170%;
  color: #232323;
`

const ErrorCount = styled.span`
  width: 40px;
  display: inline-block;
`

const SupportLink = styled.a`
  color: #232323;
  text-decoration: underline;

  &:hover {
    color: #ac0ae8;
  }
`
